import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { FilterX, Undo2 } from "lucide-react";

/**
 * SkipSuggestionsPanel — Profile-settings card that lists every keyword
 * the user has permanently dismissed from the skip-word auto-suggester,
 * with a one-click undo per row.
 *
 * Powered by `GET /api/jobs-search/skip-suggestions/dismissed` +
 * `DELETE /api/jobs-search/skip-suggestions/dismissed/{keyword}`.
 * Kept in its own component so it can live under Profile without
 * dragging in the entire JobSearchPanel dependency graph.
 */
export default function SkipSuggestionsPanel() {
  const { t } = useT();
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs-search/skip-suggestions/dismissed");
      setDismissed(data?.dismissed || []);
    } catch {
      setDismissed([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const undo = async (kw) => {
    setBusyKey(kw);
    try {
      const { data } = await api.delete(`/jobs-search/skip-suggestions/dismissed/${encodeURIComponent(kw)}`);
      setDismissed(data?.dismissed || []);
      toast.success(t("profSkipUndone", { kw }));
    } catch {
      toast.error(t("profSkipUndoFail"));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="ed-card p-6" data-testid="skip-suggestions-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="ed-eyebrow inline-flex items-center gap-2">
            <FilterX size={12} strokeWidth={1.6} /> {t("profSkipTitle")}
          </p>
          <p className="text-xs text-neutral-600 mt-1 max-w-2xl leading-relaxed">
            {t("profSkipHint")}
          </p>
        </div>
        {!loading && dismissed.length > 0 && (
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500" data-testid="skip-suggestions-count">
            {t("profSkipCount", { n: dismissed.length })}
          </span>
        )}
      </div>

      {loading ? (
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500" data-testid="skip-suggestions-loading">
          {t("profSkipLoading")}
        </p>
      ) : dismissed.length === 0 ? (
        <p className="text-sm text-neutral-500 italic" data-testid="skip-suggestions-empty">
          {t("profSkipEmpty")}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2" data-testid="skip-suggestions-list">
          {dismissed.map((kw) => (
            <li key={kw} className="inline-flex items-stretch border border-neutral-300 overflow-hidden">
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 text-neutral-700">
                {kw}
              </span>
              <button
                type="button"
                data-testid={`skip-suggestion-undo-${kw}`}
                onClick={() => undo(kw)}
                disabled={busyKey === kw}
                title={t("profSkipUndo")}
                className="px-2 border-l border-neutral-300 hover:bg-neutral-900 hover:text-[#F9F8F6] text-neutral-500 transition-colors disabled:opacity-50"
                aria-label={t("profSkipUndo")}
              >
                <Undo2 size={11} strokeWidth={1.7} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
